import { useState, useEffect } from "react";

function About() {
    const [aboutData, setAboutData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch('/api/about')
            .then(res => res.json())
            .then(data => {
                setAboutData(data);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error', err);
                // Fallback data if API fails
                setAboutData({
                    name: "Brandon Cannizzaro",
                    title: "Software Engineering Student",
                    school: "New Jersey Institute of Technology",
                    message: "Passionate about building scalable applications and learning new technologies."
                });
                setLoading(false);
            });
    }, []);

    const skills = [
        "Go", "React", "JavaScript", "Node.js", "Zig",
        "C/C++", "Python", "SQL", "PostgreSQL", "MongoDB",
        "Docker", "Git", "REST APIs", "GraphQL", "HTML/CSS"
    ];

    const securitySkills = [
        "Penetration Testing", "Web App Security", "Network Security",
        "SQL Injection", "Buffer Overflows", "Binary Exploitation",
        "Cryptography", "Bash Scripting", "Python Scripting"
    ];

    const htbModules = [
        { name: "Network Enumeration with Nmap", category: "Offensive" },
        { name: "SQL Injection Fundamentals", category: "Offensive" },
        { name: "Stack-Based Buffer Overflows", category: "Offensive" },
        { name: "File Inclusion", category: "Offensive" },
        { name: "Command Injection", category: "Offensive" },
        { name: "API Attacks", category: "Offensive" },
        { name: "Web Fuzzing", category: "Offensive" },
        { name: "Cracking Passwords with Hashcat", category: "Offensive" },
        { name: "JavaScript Deobfuscation", category: "Defensive" },
        { name: "DNS Enumeration Using Python", category: "General" },
        { name: "Introduction to Networking", category: "General" },
        { name: "Bash Scripting", category: "General" },
        { name: "Introduction to Binary Fuzzing", category: "Offensive" },
        { name: "Prompt Injection Attacks", category: "Offensive" },
    ];

    if (loading) return (
        <div className="loading">
            <div className="loading-spinner"></div>
            Loading...
        </div>
    );

    return (
        <div className="section">
            <div className="section-header">
                <span className="section-label">About Me</span>
                <h2 className="section-title">Get to Know Me</h2>
                <p className="section-description">
                    Software engineering student with a passion for both building secure applications and breaking them
                </p>
            </div>

            <div className="about-grid">
                <div className="about-image">
                    <div className="image-wrapper">
                        <span className="image-placeholder">👨‍💻</span>
                    </div>
                    <div className="about-stats">
                        <div className="stat-card">
                            <div className="stat-value">3+</div>
                            <div className="stat-label">Years Coding</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-value">10+</div>
                            <div className="stat-label">Projects</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-value">5+</div>
                            <div className="stat-label">Languages</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-value">190</div>
                            <div className="stat-label">HTB Targets</div>
                        </div>
                    </div>
                </div>

                <div className="about-content">
                    <div className="about-text">
                        <h2>{aboutData?.name}</h2>
                        <p style={{ color: 'var(--accent-primary)', fontWeight: 500, marginBottom: '1rem' }}>
                            Software Engineer & Cybersecurity Enthusiast
                        </p>
                        <p>
                            I'm a Computer Science student at NJIT with a passion for building secure 
                            applications and understanding how to break them. My interests span full-stack 
                            development, systems programming, and offensive security.
                        </p>
                        <p>
                            I've completed extensive hands-on training in penetration testing through 
                            Hack The Box Academy, achieving a <strong>Top 5% ranking</strong> with expertise 
                            in web application security, network exploitation, and binary vulnerabilities.
                        </p>
                        <p>
                            When I'm not coding or CTFing, you can find me contributing to open-source 
                            projects, exploring bleeding-edge technologies like Zig, or solving problems on LeetCode.
                        </p>
                    </div>

                    <div className="skills-section">
                        <h3>Technical Skills</h3>
                        <div className="skills-grid">
                            {skills.map((skill) => (
                                <span key={skill} className="skill-tag">
                                    {skill}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="skills-section">
                        <h3>Cybersecurity Skills</h3>
                        <div className="skills-grid">
                            {securitySkills.map((skill) => (
                                <span key={skill} className="skill-tag" style={{ 
                                    background: 'rgba(255, 95, 86, 0.1)', 
                                    borderColor: 'rgba(255, 95, 86, 0.3)',
                                    color: '#ff5f56'
                                }}>
                                    {skill}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="education-section">
                        <h3>Education & Certifications</h3>
                        <div className="education-card">
                            <h4>{aboutData?.school}</h4>
                            <p>Bachelor of Science in Computer Science</p>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                                Expected Graduation: 2026
                            </p>
                        </div>
                        
                        <div className="education-card" style={{ marginTop: '1rem', borderColor: 'rgba(0, 212, 255, 0.3)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <h4 style={{ color: 'var(--accent-primary)' }}>Hack The Box Academy</h4>
                                    <p>Cybersecurity Training - Top 5% Ranking</p>
                                </div>
                                <span style={{ 
                                    background: 'rgba(0, 212, 255, 0.1)', 
                                    color: 'var(--accent-primary)',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '20px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600
                                }}>
                                    14 Modules Completed
                                </span>
                            </div>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.75rem' }}>
                                <strong>190</strong> targets compromised • Student ID: #1317851
                            </p>
                            <div className="skills-grid" style={{ marginTop: '0.75rem' }}>
                                {htbModules.slice(0, 6).map((module) => (
                                    <span key={module.name} className="skill-tag" style={{ fontSize: '0.75rem' }}>
                                        {module.name}
                                    </span>
                                ))}
                                <span className="skill-tag" style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                                    +8 more...
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default About;
